/**
 * هوية المنصة — the Super Admin's two marks and the platform's name.
 *
 * Whatever is uploaded here replaces the built-in marks on every screen of
 * the system, for every account, before and after sign-in:
 *
 *   شعار جهة التطوير  ABBAS JR Developments' place — top of every dashboard
 *                     and above the sign-in card
 *   شعار المنصة       Capsule's place — heading the sidebar, inside the
 *                     sign-in card, and on the landing page
 *
 * Each mark is drawn whole, at its own proportions, on the plate picked here
 * (domain/logoBackdrop.js), so a wide wordmark is not squeezed into a square
 * and a logo in dark lines does not vanish on the dark themes. «استعادة
 * الأصلي» goes back to the built-in mark. Backend/lib/branding.js stores it.
 */

import { useEffect, useRef, useState } from 'react';
import { Button, Field, Icon, Input, Panel } from '../../components/common';
import { HeroBand } from '../../components/common/Tile3D';
import { brandingApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { applyBranding, useBranding } from '../../services/branding';
import { BUILT_IN } from '../../domain/branding';
import { LOGO_BACKDROPS, inspectLogo, logoInkAdvice, logoPlateClass } from '../../domain/logoBackdrop';
import { compressImage, formatBytes } from '../../domain/files';
import { BackdropOption } from '../hospital/StaffAccountTools';

/** Sharp on a 2x screen at the largest place a mark is drawn, and small enough to serve. */
const MAX_EDGE = 1600;
/** What the server accepts (Backend/lib/branding.js MAX_LOGO_BYTES). */
const MAX_BYTES = 2 * 1024 * 1024;
const KEPT_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

const SLOTS = [
  {
    id: 'agency',
    title: 'شعار جهة التطوير',
    icon: 'badge-check',
    replaces: 'ABBAS JR Developments',
    where: 'أعلى كل لوحة تحكم بجانب اسم المستخدم، وفوق بطاقة تسجيل الدخول، وفي الصفحة التعريفية.',
  },
  {
    id: 'product',
    title: 'شعار المنصة',
    icon: 'layout-dashboard',
    replaces: 'كبسولة',
    where: 'أعلى القائمة الجانبية في كل اللوحات، وداخل بطاقة تسجيل الدخول، وفي الصفحة التعريفية.',
  },
];

const backdropName = (id) => LOGO_BACKDROPS.find((b) => b.id === id)?.name || '';

const readAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('تعذر قراءة الصورة'));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(file);
  });

const imageSize = (src) =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => resolve(null);
    image.src = src;
  });

/**
 * The file as the server will keep it: as uploaded when it is already a PNG,
 * JPG or WebP within the edge and the size, else redrawn as a PNG within the
 * edge — PNG so a transparent background stays transparent.
 */
async function prepare(file) {
  const original = await readAsDataUrl(file);
  const size = await imageSize(original);
  if (!size) throw new Error('تعذر قراءة الصورة — جرّب ملف PNG أو JPG');
  const fits = Math.max(size.width, size.height) <= MAX_EDGE && file.size <= MAX_BYTES;
  if (fits && KEPT_TYPES.includes(file.type)) return original;
  const redrawn = await compressImage(file, { maxWidth: MAX_EDGE, maxHeight: MAX_EDGE, mimeType: 'image/png' });
  if (Math.floor(((redrawn.length - redrawn.indexOf(',') - 1) * 3) / 4) > MAX_BYTES) {
    throw new Error('الشعار أكبر من 2 ميجابايت حتى بعد تصغيره — اختر صورة أبسط');
  }
  return redrawn;
}

/** One mark: its preview, upload, reset, and the plate it is drawn on. */
function SlotCard({ slot, mark, onSaved }) {
  const { success, error: toastError } = useNotifications();
  const [busy, setBusy] = useState(false);
  const [savingBackdrop, setSavingBackdrop] = useState(null);
  const [reading, setReading] = useState(null);
  const [bytes, setBytes] = useState(null);
  const input = useRef(null);

  useEffect(() => {
    let live = true;
    setReading(null);
    setBytes(null);
    if (mark.custom) {
      inspectLogo(mark.src).then((r) => live && setReading(r));
      fetch(mark.src)
        .then((r) => r.blob())
        .then((b) => live && setBytes(b.size))
        .catch(() => {});
    }
    return () => {
      live = false;
    };
  }, [mark.custom, mark.src]);

  const save = async (payload, message) => {
    const summary = await brandingApi.update(payload);
    applyBranding(summary);
    onSaved?.();
    success(message);
  };

  const choose = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toastError('اختر صورة (PNG أو JPG أو WebP)');
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await prepare(file);
      // A new mark gets the plate its own colours call for; one in its own
      // background still gets the white one, which frames it on every theme.
      const found = await inspectLogo(dataUrl);
      const plate = found && found.suggestion !== 'none' ? found.suggestion : 'light';
      await save(
        { [slot.id]: dataUrl, [`${slot.id}Backdrop`]: plate },
        `تم حفظ ${slot.title} — يظهر الآن في كل صفحات النظام بدل ${slot.replaces}، على «${backdropName(plate)}»`,
      );
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    setBusy(true);
    try {
      await save({ [slot.id]: null }, `عاد ${slot.title} إلى الشعار الأصلي (${slot.replaces})`);
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const pickBackdrop = async (id) => {
    if (id === mark.backdrop || savingBackdrop) return;
    setSavingBackdrop(id);
    try {
      await save({ [`${slot.id}Backdrop`]: id }, `تم حفظ خلفية ${slot.title}: ${backdropName(id)}`);
    } catch (err) {
      toastError(err.message);
    } finally {
      setSavingBackdrop(null);
    }
  };

  const advice = mark.custom ? logoInkAdvice(reading) : null;

  return (
    <Panel className="brand-panel">
      <div className="flex flex-wrap items-center gap-5">
        <div className="brand-well" aria-live="polite">
          {mark.custom ? (
            <img src={mark.src} alt={mark.alt} className={`brand-upload brand-well__logo ${logoPlateClass(mark.backdrop)}`} />
          ) : (
            <img src={mark.src} alt={mark.alt} className="brand-mark brand-well__logo" />
          )}
        </div>
        <div className="min-w-[min(100%,14rem)] flex-1">
          <h2 className="flex items-center gap-2 text-base font-bold text-white">
            <Icon name={slot.icon} size={18} className="text-cyan-300" /> {slot.title}
          </h2>
          <p className="mt-1 text-sm text-slate-400">{slot.where}</p>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
            {mark.custom ? (
              <>
                <span className="rounded-md bg-emerald-500/10 px-1.5 py-0.5 text-emerald-300">مرفوع — بدل {slot.replaces}</span>
                {bytes != null && (
                  <span>
                    الحجم <span dir="ltr">{formatBytes(bytes)}</span>
                  </span>
                )}
                {reading?.transparent && <span className="rounded-md bg-white/5 px-1.5 py-0.5 text-slate-400">PNG بخلفية شفافة</span>}
              </>
            ) : (
              <span className="rounded-md bg-white/5 px-1.5 py-0.5 text-slate-400">الشعار الأصلي: {slot.replaces}</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            aria-label={`ملف ${slot.title}`}
            onChange={choose}
          />
          <Button icon="upload" loading={busy} onClick={() => input.current?.click()}>
            {mark.custom ? 'تغيير الشعار' : 'رفع شعار جديد'}
          </Button>
          {mark.custom && (
            <Button variant="ghost" icon="rotate-ccw" disabled={busy} onClick={restore}>
              استعادة الأصلي
            </Button>
          )}
        </div>
      </div>

      {mark.custom && (
        <div className="mt-5 border-t border-white/5 pt-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 text-sm font-bold text-white">
              <Icon name="contrast" size={16} className="text-cyan-300" /> خلفية الشعار
            </h3>
            {reading && reading.suggestion !== 'none' && reading.suggestion !== mark.backdrop && (
              <Button variant="subtle" icon="sparkles" disabled={Boolean(savingBackdrop)} onClick={() => pickBackdrop(reading.suggestion)}>
                استخدم المقترح: {backdropName(reading.suggestion)}
              </Button>
            )}
          </div>
          <p className="mt-1 text-xs text-slate-400">
            الشعار يظهر على كل المظاهر، الداكنة والفاتحة. اختر الخلفية التي يبقى عليها واضحاً في الاثنين — المعاينة تعرضه على الاثنين.
          </p>
          <div className="brand-backdrops mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3" role="radiogroup" aria-label={`خلفية ${slot.title}`}>
            {LOGO_BACKDROPS.map((option, index) => (
              <BackdropOption
                key={option.id}
                option={option}
                logo={mark.src}
                index={index}
                active={option.id === mark.backdrop}
                suggested={reading?.suggestion === option.id && option.id !== 'none'}
                saving={savingBackdrop === option.id}
                onPick={pickBackdrop}
              />
            ))}
          </div>
          {advice && (
            <p className="mt-3 flex items-start gap-2 text-xs text-slate-400">
              <Icon name="lightbulb" size={14} className="mt-0.5 shrink-0 text-amber-300" />
              {advice}
            </p>
          )}
        </div>
      )}
    </Panel>
  );
}

/** The platform's name, as the welcome, the sidebar and the printouts say it. */
function NameCard({ brand }) {
  const { success, error: toastError } = useNotifications();
  const [name, setName] = useState(brand.named ? brand.name : '');
  const [busy, setBusy] = useState(false);
  useEffect(() => setName(brand.named ? brand.name : ''), [brand.named, brand.name]);

  const save = async () => {
    setBusy(true);
    try {
      applyBranding(await brandingApi.update({ name: name.trim() }));
      success(name.trim() ? `اسم المنصة الآن: ${name.trim()}` : 'عاد اسم المنصة إلى الافتراضي');
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel>
      <h2 className="flex items-center gap-2 text-base font-bold text-white">
        <Icon name="pencil" size={18} className="text-cyan-300" /> اسم المنصة
      </h2>
      <p className="mt-1 text-sm text-slate-400">
        يظهر في ترحيب شاشة الدخول، واسم المساعد الذكي، وسطر «Powered by» أسفل الروشتة والملف الطبي المطبوعين. اتركه فارغاً لاسم «
        {BUILT_IN.name}» — أو بلا اسم إن رفعت شعاراً جديداً للمنصة.
      </p>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <Field label="الاسم" className="min-w-[min(100%,16rem)] flex-1">
          <Input value={name} maxLength={40} placeholder={BUILT_IN.name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Button icon="save" loading={busy} disabled={name.trim() === (brand.named ? brand.name : '')} onClick={save}>
          حفظ الاسم
        </Button>
      </div>
    </Panel>
  );
}

/** Where the marks land: the top bar and sidebar, and the sign-in card, on a dark and a light surface. */
function Preview({ brand }) {
  const draw = (mark, cls) =>
    mark.custom ? (
      <img src={mark.src} alt="" className={`brand-upload ${logoPlateClass(mark.backdrop)} ${cls}`} />
    ) : (
      <img src={mark.src} alt="" className={`brand-mark ${cls}`} />
    );
  return (
    <Panel>
      <h2 className="flex items-center gap-2 text-base font-bold text-white">
        <Icon name="eye" size={18} className="text-cyan-300" /> معاينة
      </h2>
      <p className="mt-1 text-sm text-slate-400">كما يراها كل مستخدم الآن، في المظهر الداكن والفاتح.</p>
      <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
        {['dark', 'light'].map((tone) => (
          <div key={tone} className={`brand-preview brand-preview--${tone}`}>
            <div className="brand-preview__bar">
              {draw(brand.product, 'brand-preview__product')}
              <span className="flex-1" />
              {draw(brand.agency, 'brand-preview__agency')}
            </div>
            <div className="brand-preview__card">
              {draw(brand.agency, 'brand-preview__hero')}
              {draw(brand.product, 'brand-preview__product')}
              <b>{brand.name ? `مرحباً بك في ${brand.name}` : 'مرحباً بك'}</b>
            </div>
            <small>{tone === 'dark' ? 'مظهر داكن' : 'مظهر فاتح'}</small>
          </div>
        ))}
      </div>
    </Panel>
  );
}

export function AdminBranding() {
  const brand = useBranding();
  return (
    <div className="space-y-6">
      <HeroBand
        icon="palette"
        tone="purple"
        title="هوية المنصة"
        subtitle="الشعاران اللذان يظهران في كل صفحات النظام لكل المستخدمين. ارفع شعارك فيحل محل الشعار الأصلي في كل مكان فوراً، واختر خلفيته حتى يبقى واضحاً على كل المظاهر."
      />
      {!brand.ready ? null : (
        <>
          {SLOTS.map((slot) => (
            <SlotCard key={slot.id} slot={slot} mark={brand[slot.id]} />
          ))}
          <Preview brand={brand} />
          <NameCard brand={brand} />
        </>
      )}
    </div>
  );
}
